/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatMenuTrigger } from '@angular/material/menu';
import { TooltipPosition } from '@angular/material/tooltip';
import { asyncScheduler, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';


@Component({
  selector: 'neo-multi-select',
  templateUrl: './neo-multi-select.component.html',
  styleUrls: ['./neo-multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: NeoMultiSelectComponent,
      multi: true
    }
  ],
  standalone: false
})
export class NeoMultiSelectComponent implements ControlValueAccessor, OnChanges, OnInit, OnDestroy {
  @Input() options: Array<any> = []; // Array of options to display
  @Input() optionLabel: string = 'label'; // key in the option object to display as label
  @Input() optionValue: string = 'id'; // key in the option object to use as value
  @Input() filterBy: string = 'label'; // key in the option object to filter by
  @Input() ellipsifyOptionText: boolean = false; // Whether to show ellipsis... (3 dots) in case of long text in options
  @Input() optionTooltipPosition: TooltipPosition = 'after'; // Position of the tooltip for options
  @Input() showSelectedCount: boolean = false; // Whether to show the count of selected options
  @Input() placeholder: string = ''; // Placeholder text for the select trigger
  @Input() searchPlaceholder: string = ''; // Placeholder text for the search input
  @Input() hideSearch: boolean = false; // Whether to hide the search input
  @Input() hideSelectAll: boolean = false; // Whether to hide the select all option
  @Input() selectAllLabel: string = 'Select All'; // Label for the select all checkbox row
  @Input() includeResetApplyButtons: boolean = false; // Whether to include Reset and Apply buttons
  @Input() panelClass: string[] = []; // Additional classes to apply to the dropdown panel
  @Input() backdropClass: string = ''; // Additional classes to apply to the backdrop of the dropdown panel
  @Input() optionsContainerClass: string[] = []; // Additional classes to apply to the options container
  @Input() triggerTemplate?: TemplateRef<any>; // Custom template for the trigger button
  @Input() triggerLabelClass?: string[]; // Additional classes to apply to the trigger label
  @Input() required: boolean = false; // Whether the neo mat select control is required
  @Input() disabled: boolean = false; // Whether the neo mat select control is disabled
  @Input() hasError: boolean = false; // Whether the neo mat select control has an error
  @Input() hint: string | null = null; // Optional hint under the field, shown with an info icon
  @Input() returnFullObject: boolean = false; // Whether to return the full object or just the value
  @Input() appearance: MatFormFieldAppearance = 'outline'; // Appearance of the neo mat select control
  @Input() showSelectedValuesCountOnly: boolean = false; // Whether to display only the count of selected values
  @Input() showSelectedValuesCountAlways: boolean = false; // Whether to always show the count of selected values, even when all are selected
  @Input() displayedSelectedValuesPredicate: (value: any[]) => string = this.displayedSelectedValues.bind(this); // Function to display selected values
  @Input() resetClicked?: () => void;
  @Input() applyClicked?: () => void;
  @Input() matchTriggerWidth: boolean = false; // Whether to size the dropdown panel to match the trigger element's width
  @Input() selectAllCheckedColor: 'primary' | 'accent' | 'warn' = 'primary'; // Checkbox color when all options are selected
  @Input() selectAllIndeterminateColor: 'primary' | 'accent' | 'warn' = 'warn'; // Checkbox color when only some options are selected (indeterminate state)
  @Input() showPlaceholderWhenAllSelected: boolean = false; // When true, hide label and show placeholder when all options are selected

  @Input() value: any[] = []; // Selected values from the options
  @Output() valueChange = new EventEmitter<any[]>(); // Event emitted when the selected values change


  @Output() panelShow = new EventEmitter<void>(); // Event emitted when the dropdown panel is shown
  @Output() panelHide = new EventEmitter<void>(); // Event emitted when the dropdown panel is hidden

  @ViewChild(MatMenuTrigger, { static: false }) menuTrigger!: MatMenuTrigger;
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>;

  allSelected: boolean | null = false;
  internalValue: any[] = []; // Pending checkbox state used in staged mode (includeResetApplyButtons=true)
  searchCtrl = new FormControl('');
  filteredOptions: Array<any> = [];
  triggerCtrl = new FormControl('');

  private destroyed = new Subject();

  constructor(private elementRef: ElementRef) {}

  public get selectedCount(): number {
    return this.value.filter((val) => this.findIndex(this.options, val) > -1).length;
  }

  public get triggerAllSelected(): boolean {
    return this.options.length > 0 && this.value.length === this.options.length;
  }

  public get effectiveLabelText(): string {
    if (!this.value?.length) return this.placeholder;
    if (this.showPlaceholderWhenAllSelected && this.triggerAllSelected) return this.placeholder;
    return this.displayedSelectedValuesPredicate(this.value) || this.placeholder;
  }

  // ControlValueAccessor implementation
  onChange: (value: any[]) => void = (_value: any[]) => { /* default implementation */ };
  onTouched: () => void = () => { /* Implement any logic needed when the component is touched */ };

  writeValue(value: any[]): void {
    this.value = value || [];
    if (this.includeResetApplyButtons) {
      this.internalValue = [...this.value]; // Keep staged state in sync with the committed value
    }
    this.checkAllSelected(); // Ensure allSelected is updated when value is set
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.options = [...this.options];
    const search = this.searchCtrl.value || '';
    if (search) {
      this.filterOptions(search); // Re-apply active search filter against the updated options list
    } else {
      this.filteredOptions = [...this.options];
    }
    if (this.includeResetApplyButtons && changes.value) {
      this.internalValue = [...this.value]; // Re-sync staged state when the committed value changes externally
    }
    this.onChange(this.value); // Update value when options change
    this.checkAllSelected(); // Update allSelected when options change
  }

  ngOnInit(): void {
    this.filteredOptions = [...this.options];
    this.searchCtrl.valueChanges.pipe(takeUntil(this.destroyed)).subscribe((value) => this.filterOptions(value || ''));
    this.checkAllSelected(); // Ensure allSelected is updated on initialization
  }

  // Select/Deselect all filtered options
  toggleSelectAll(): void {
    if (this.includeResetApplyButtons) {
      // Staged mode: modify internalValue only; changes are committed on Apply
      if (this.allSelected) {
        this.filteredOptions.forEach((option) => {
          const index = this.findIndex(this.internalValue, option);
          if (index !== -1) {
            this.internalValue.splice(index, 1);
          }
        });
      } else {
        this.filteredOptions.forEach((option) => {
          if (this.findIndex(this.internalValue, option) === -1) {
            const selValue = this.returnFullObject ? option : option[this.optionValue];
            this.internalValue.push(selValue);
          }
        });
      }
      this.checkAllSelected();
    } else {
      // Immediate mode: modify value directly and emit
      if (this.allSelected) {
        this.filteredOptions.forEach((option) => {
          const index = this.findIndex(this.value, option);
          if (index !== -1) {
            this.value.splice(index, 1);
          }
        });
      } else {
        this.filteredOptions.forEach((option) => {
          if (this.findIndex(this.value, option) === -1) {
            const selValue = this.returnFullObject ? option : option[this.optionValue];
            this.value.push(selValue);
          }
        });
      }
      this.onChange(this.value);
      this.onTouched();
      this.valueChange.emit(this.value);
      this.checkAllSelected();
    }
  }

  // Check if all filtered options are selected
  checkAllSelected(): void {
    const checkValue = this.includeResetApplyButtons ? this.internalValue : this.value; // Use staged or committed value depending on mode
    const totalOptions = this.filteredOptions.length;
    const selectedCount = this.filteredOptions.filter(option => this.findIndex(checkValue, option) > -1).length;

    if (selectedCount > 0 && selectedCount === totalOptions) {
      this.allSelected = true; // All options are selected
    } else if (selectedCount === 0) {
      this.allSelected = false; // No options are selected
    } else {
      this.allSelected = null; // Some options are selected (indeterminate)
    }
  }

  // Select/Deselect an option
  toggleSelection(value: any): void {
    if (this.includeResetApplyButtons) {
      // Staged mode: modify internalValue only; changes are committed on Apply
      const index = this.findIndex(this.internalValue, value);
      if (index > -1) {
        this.internalValue.splice(index, 1);
      } else {
        this.internalValue.push(this.returnFullObject ? value : this.getOptionValue(value));
      }
      this.checkAllSelected();
    } else {
      // Immediate mode: modify value directly and emit
      const index = this.findIndex(this.value, value);
      if (index > -1) {
        this.value.splice(index, 1);
      } else {
        const selValue = this.returnFullObject ? value : this.getOptionValue(value);
        this.value.push(selValue);
      }
      // Update value and notify parent
      this.onChange(this.value);
      this.onTouched();
      this.valueChange.emit(this.value);
      this.checkAllSelected();
    }
  }

  // Filter options based on search text
  filterOptions(searchText: string): void {
    this.filteredOptions = this.options.filter((option) =>
      option[this.filterBy].toLowerCase().includes(searchText.toLowerCase())
    );
    this.checkAllSelected();
  }

  // Open the dropdown
  open(): void {
    this.menuTrigger.openMenu();
  }

  // Close the dropdown
  close(): void {
    this.menuTrigger.closeMenu();
  }

  onResetClicked(): void {
    this.resetClicked?.(); // Delegate reset logic to the parent-provided callback
    this.menuTrigger.closeMenu();
  }

  onApplyClicked(): void {
    if (this.includeResetApplyButtons) {
      // Commit staged internalValue to the real value and notify parent
      this.value = [...this.internalValue];
      this.onChange(this.value);
      this.onTouched();
      this.valueChange.emit(this.value);
    }
    this.applyClicked?.(); // Delegate any additional apply logic to the parent-provided callback
    this.menuTrigger.closeMenu();
  }

  // Emit panelShow and panelHide events
  onOpen(): void {
    this.panelShow.emit();
    asyncScheduler.schedule(() => {
      this.searchInput?.nativeElement.focus();
      if (this.matchTriggerWidth) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const overlayRef = (this.menuTrigger as any)._overlayRef;
        if (overlayRef) {
          const triggerWidth = this.elementRef.nativeElement.getBoundingClientRect().width;
          overlayRef.updateSize({ width: triggerWidth });
          overlayRef.updatePosition();
          const panel = overlayRef.overlayElement?.querySelector('.mat-mdc-menu-panel') as HTMLElement | null;
          if (panel) {
            panel.style.maxWidth = 'unset';
            panel.style.width = '100%';
          }
        }
      }
    });
  }

  onClose(): void {
    this.panelHide.emit();
    if (this.includeResetApplyButtons) {
      this.internalValue = [...this.value]; // Discard any unapplied staged changes when panel closes without Apply
      this.checkAllSelected();
    }
  }

  displayedSelectedValues(value: any[]): string {
    return this.showSelectedValuesCountOnly
      ? (value.length ? `${this.showSelectedValuesCountAlways && value.length === this.options.length ? 'All' : value.length} selected` : '')
      : value
        .filter((val) => this.findIndex(this.options, val) > -1)
        .map((val) => String(this.options[this.findIndex(this.options, val)][this.optionLabel] || ''))
        .sort()
        .join(', ');
  }

  getOptionValue(option: any): any {
    return typeof option !== 'object' ? option : option[this.optionValue];
  }

  findIndex(value: any[], option: any): number {
    const optVal = this.getOptionValue(option);
    return value.findIndex(val => typeof val !== 'object' ? val === optVal : val[this.optionValue] === optVal);
  }

  ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.complete();
  }
}
